/**
 * VH-219: ask where the athlete is training, once, when a session is opened.
 *
 * VH-212 put the session's location in a dropdown pre-filled from the MODULE —
 * easy to scroll past exactly at an away gym. Three features read that value:
 * VH-212's substitute picker, VH-206's "which machine?" question and VH-207's
 * prefilled numbers. An unnoticed default puts all three on the wrong gym, so
 * the capture sheet now asks before logging.
 *
 * Deliberate properties:
 *
 * 1. **Pre-selected.** The module's gym is already chosen, so at the usual gym
 *    confirming is one tap.
 * 2. **Only with a real choice.** Fewer than two training locations and there is
 *    nothing to ask — the same threshold VH-207 uses for variants.
 * 3. **A confirmed answer lives on the session, not the device.** Confirming is
 *    VH-212's existing PUT with the chosen id, even when it is the module's own
 *    gym. That turns "derived" into athlete-confirmed (`location_is_override`),
 *    which is precisely what stops the question appearing again — on any device.
 * 4. **A skip asserts nothing, so it is not sent.** It is remembered locally for
 *    this session only; if storage is unavailable the question returns on
 *    reload rather than breaking the sheet.
 * 5. **A failed save is never mistaken for a confirmed one.** On error the
 *    question stays, with the reason.
 *
 * The caller decides eligibility for signed-in, unlocked sessions; this
 * component decides only whether there is something to ask.
 */
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { trainingLocationsAPI } from '../../services/trainingLocations'
import { weeklyProgramsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

const skipKey = (token) => `vh219.location-skip.${token}`

function wasSkipped(token) {
  try {
    return window.localStorage.getItem(skipKey(token)) === '1'
  } catch {
    return false
  }
}

function rememberSkip(token) {
  try {
    window.localStorage.setItem(skipKey(token), '1')
  } catch {
    // Storage unavailable: the question simply returns on reload.
  }
}

export function SessionLocationPrompt({ token, locationId, sessionName = null, isOverride = false, onDone }) {
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() => wasSkipped(token))
  const [selected, setSelected] = useState(locationId ?? null)
  const [error, setError] = useState(null)

  const { data: locationsData, isLoading } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })
  const locations = locationsData?.locations || []

  const mutation = useMutation({
    mutationFn: (id) => weeklyProgramsAPI.setCaptureSessionLocation(token, id),
    onSuccess: (res) => {
      setError(null)
      onDone?.(res?.data ?? {})
    },
    onError: (err) => setError(apiErrorMessage(err, t('capture.sessionLocation.error'))),
  })

  // Nothing to ask: already answered, skipped here, still loading, or only one
  // place the athlete could be.
  if (isOverride || dismissed || isLoading || locations.length < 2) return null

  const skip = () => {
    rememberSkip(token)
    setDismissed(true)
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-location-prompt-title"
      data-testid="session-location-prompt"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-gray-900/50 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-surface-container p-5 shadow-xl">
        <h2
          id="session-location-prompt-title"
          className="flex items-center text-lg font-semibold text-gray-900"
        >
          <MapPinIcon className="mr-2 h-5 w-5 text-primary-600" />
          {t('capture.sessionLocationPrompt.title')}
        </h2>
        {/* VH-232: in the week overview many sessions are listed, so the
            question has to say which one it is about. On the capture sheet the
            page is one session and the caller passes nothing. */}
        {sessionName && (
          <p className="mt-1 text-sm font-medium text-gray-900" data-testid="session-location-prompt-session">
            {t('capture.sessionLocationPrompt.forSession', { name: sessionName })}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-600">{t('capture.sessionLocationPrompt.body')}</p>

        <fieldset className="mt-4 space-y-2">
          <legend className="sr-only">{t('capture.sessionLocationPrompt.title')}</legend>
          {locations.map((loc) => (
            <label
              key={loc.id}
              className={`flex cursor-pointer items-center rounded-md border px-3 py-3 text-base ${
                selected === loc.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="session-location-prompt"
                value={loc.id}
                checked={selected === loc.id}
                onChange={() => {
                  setError(null)
                  setSelected(loc.id)
                }}
                className="mr-3 h-4 w-4 text-primary-600"
              />
              <span className="text-gray-900">{loc.name}</span>
              {loc.id === locationId && (
                <span className="ml-auto text-xs text-gray-500">
                  {t('capture.sessionLocationPrompt.fromModule')}
                </span>
              )}
            </label>
          ))}
        </fieldset>

        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="session-location-prompt-error">
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            onClick={skip}
            disabled={mutation.isPending}
            className="text-sm text-gray-600 underline disabled:opacity-50"
          >
            {t('capture.sessionLocationPrompt.skip')}
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate(selected)}
            disabled={selected == null || mutation.isPending}
            className="btn-primary disabled:opacity-50"
          >
            {mutation.isPending
              ? t('capture.sessionLocationPrompt.saving')
              : t('capture.sessionLocationPrompt.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SessionLocationPrompt
